"use client";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Stripe } from "@stripe/stripe-js";
import { resolveStripeErrorMessage } from "../utils/resolveStripeErrorMessage";
import {
  consumeSetupIntentRedirect,
  peekSetupIntentRedirect,
} from "../utils/setupIntentRedirect";

interface Props {
  stripe: Stripe | null;
  onSuccess: (setupIntentId: string) => Promise<void>;
  onError?: () => void;
  setErrorMessage: (message: string) => void;
}

export function useStripeSetupRedirect({
  stripe,
  onSuccess,
  onError,
  setErrorMessage,
}: Props) {
  const [isResolvingRedirect, setIsResolvingRedirect] = useState(
    peekSetupIntentRedirect,
  );
  const callbacksRef = useRef({ onSuccess, onError, setErrorMessage });
  useLayoutEffect(() => {
    callbacksRef.current = { onSuccess, onError, setErrorMessage };
  });

  const handledRef = useRef(false);
  useEffect(() => {
    if (!isResolvingRedirect || !stripe || handledRef.current) return;

    const clientSecret = consumeSetupIntentRedirect();
    if (!clientSecret) {
      // Already handled by an earlier mount — show the form as usual.
      setIsResolvingRedirect(false);
      return;
    }

    handledRef.current = true;

    void (async () => {
      const { setupIntent, error } =
        await stripe.retrieveSetupIntent(clientSecret);
      const callbacks = callbacksRef.current;

      if (setupIntent?.status === "succeeded") {
        try {
          await callbacks.onSuccess(setupIntent.id);
        } catch {
          callbacks.onError?.();
        }
      } else {
        callbacks.onError?.();
        callbacks.setErrorMessage(
          resolveStripeErrorMessage(
            error ?? setupIntent?.last_setup_error,
            "useStripeSetupRedirect",
          ),
        );
      }

      setIsResolvingRedirect(false);
    })();
  }, [isResolvingRedirect, stripe]);

  return { isResolvingRedirect };
}
